"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { SignedIn, SignedOut, UserButton, useUser } from "@clerk/clerk-react";
import { useIsExpert } from "@/lib/use-is-expert";
import { ATOMCAMP_URL, BOOK_DEMO_HREF, GET_STARTED_HREF } from "@/lib/marketing-links";

type NavItem = {
  label: string;
  href: string;
  /**
   * A line under the label. Only atomcamp has one: it is the one link that leaves inaraX, so it
   * needs saying what it is; every other entry is just its page name.
   */
  description?: string;
  /** Opens in a new tab: the link leaves inaraX. */
  external?: boolean;
};

/** A titled column in a mega panel; its items stack vertically under the title. */
type NavSection = {
  title: string;
  href: string;
  /** May be empty: the title alone then links to the section. */
  items: readonly NavItem[];
};

type NavMenu = {
  label: string;
  /** The mega panel's heading, when it should read differently from the navbar label. */
  heading?: string;
  /** The menu's own page: clicking the menu name goes here, the chevron opens the dropdown. */
  href: string;
  /** Left out of the navbar for now; the menu and its pages stay as they are. */
  hidden?: boolean;
} & (
  | { items: readonly NavItem[] }
  /** A menu spanning several pages splits its panel into one column per section. */
  | { sections: readonly NavSection[] }
);

/**
 * The enterprise sitemap. On desktop, clicking a menu opens a full-width mega panel; its
 * heading links to the menu's own page. Each menu has its own page, and its items jump to sections of it
 * (`/product#…`), so renaming a section id on one of those pages means updating it here too. Product
 * links to /product, /how-it-works and the FAQ at the end of /product.
 */
const NAV_MENUS: readonly NavMenu[] = [
  {
    label: "Product",
    heading: "Product Overview",
    href: "/product",
    items: [
      {
        label: "Product",
        href: "/product",
      },
      {
        label: "How It Works",
        href: "/how-it-works",
      },
      {
        label: "FAQs",
        href: "/product#faq",
      },
    ],
  },
  {
    label: "Solutions",
    href: "/solutions",
    hidden: true,
    items: [
      {
        label: "Enterprise AI Upskilling",
        href: "/solutions#enterprise-ai-upskilling",
      },
      {
        label: "Role-Based Training",
        href: "/solutions#role-based-training",
      },
      {
        label: "Custom Programs",
        href: "/solutions#custom-programs",
      },
    ],
  },
  {
    label: "Industries",
    heading: "Industries Overview",
    href: "/industries",
    items: [
      {
        label: "Public Sector",
        href: "/industries#public-sector",
      },
      {
        label: "Education",
        href: "/industries#education",
      },
      {
        label: "Manufacturing",
        href: "/industries#manufacturing",
      },
      {
        label: "Banking & Finance",
        href: "/industries#banking",
      },
    ],
  },
  {
    label: "Resources",
    href: "/case-studies",
    hidden: true,
    items: [
      {
        label: "Case Studies",
        href: "/case-studies",
      },
      {
        label: "FAQs",
        href: "/faqs",
      },
    ],
  },
  {
    label: "About Us",
    href: "/about",
    sections: [
      {
        title: "Mission",
        href: "/about#mission",
        items: [],
      },
      {
        title: "Our Story",
        href: "/about#our-story",
        items: [
          {
            label: "atomcamp",
            href: ATOMCAMP_URL,
            description: "Our parent company",
            external: true,
          },
        ],
      },
      {
        title: "Leadership",
        href: "/about#leadership",
        items: [],
      },
      {
        title: "Global Footprint",
        href: "/about#global-footprint",
        items: [],
      },
    ],
  },
];

/** The menus the navbar actually shows, desktop and mobile alike. */
const VISIBLE_MENUS = NAV_MENUS.filter((menu) => !menu.hidden);

/** Every link in a menu, across its sections. */
function menuItems(menu: NavMenu): readonly NavItem[] {
  return "sections" in menu ? menu.sections.flatMap((section) => section.items) : menu.items;
}

/**
 * A menu reads as current when one of its items lives on the page you are on. External links
 * (atomcamp) are skipped, as is the demo form: linking to /b2b does not make it part of a menu.
 */
function isMenuActive(pathname: string, menu: NavMenu) {
  if (pathname === menu.href) return true;
  return menuItems(menu).some((item) => {
    if (item.external || item.href === BOOK_DEMO_HREF) return false;
    const path = item.href.split("#")[0];
    return pathname === path || pathname.startsWith(`${path}/`);
  });
}

/** One entry in the mega menu panel: the page name, with its description (if any) under it. */
function MegaMenuLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: () => void;
}) {
  const content = (
    <>
      <span className="inline-flex items-center gap-1 font-space-grotesk font-semibold text-mkt-on-surface underline-offset-4 decoration-mkt-primary-container group-hover:text-mkt-primary-container group-hover:underline">
        {item.label}
        {item.external ? (
          <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
        ) : null}
      </span>
      {item.description ? (
        <span className="mt-1 block text-sm text-mkt-on-surface-variant">
          {item.description}
        </span>
      ) : null}
    </>
  );
  const className =
    "group block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mkt-primary-container";

  return item.external ? (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      className={className}
    >
      {content}
    </a>
  ) : (
    <Link href={item.href} onClick={onNavigate} className={className}>
      {content}
    </Link>
  );
}

/** One entry in a mobile accordion: just the page name. */
function MobileMenuLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: () => void;
}) {
  const className =
    "block py-2 text-mkt-on-surface-variant hover:text-mkt-primary transition-colors font-space-grotesk";

  return item.external ? (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      className={className}
    >
      {item.label}
    </a>
  ) : (
    <Link href={item.href} onClick={onNavigate} className={className}>
      {item.label}
    </Link>
  );
}

const bookDemoClassName =
  "bg-mkt-primary-container text-mkt-on-primary px-6 py-2 rounded-full font-bold font-space-grotesk text-sm whitespace-nowrap transition-transform hover:-translate-y-0.5 active:scale-95";

/**
 * Fixed top navigation bar used across the marketing pages. Pages that render
 * this must add `pt-20` (or equivalent) to their top-level content, since a
 * `fixed` element is taken out of document flow.
 */
export default function Navbar() {
  const { user } = useUser();
  const is_expert = useIsExpert();
  const pathname = usePathname() ?? "/";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Which menu's mega panel is open on desktop (one at a time); on mobile, which accordion is expanded.
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openMobileMenu, setOpenMobileMenu] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const openMenuData =
    VISIBLE_MENUS.find((menu) => menu.label === openMenu) ?? null;
  const closeMegaMenu = () => setOpenMenu(null);

  // Close the mega panel on Escape or a click anywhere outside the navbar and panel.
  useEffect(() => {
    if (!openMenu) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [openMenu]);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileMenu(null);
  };

  return (
    <nav
      ref={navRef}
      className="fixed top-0 w-full z-50 bg-mkt-navbar px-margin-mobile md:px-margin-desktop"
    >
      {/* Exactly h-20: LandingPageShell offsets every page by pt-20 for this fixed bar. A
          shorter bar (it was ~70px with py-4) leaves a strip of page background between
          it and a dark hero, which read as a border. */}
      <div className="flex items-center justify-between gap-6 h-20 max-w-[1440px] mx-auto">
        <Link
          href="/"
          className="shrink-0 flex items-center gap-3"
          aria-label="inaraX — Enterprise AI learning platform"
        >
          <Image
            src="/images/logo-v2.png"
            alt="inaraX"
            width={320}
            height={96}
            className="w-32 h-auto object-contain"
            priority
          />
          {/* Hidden between lg and xl, where the full desktop menu needs the room. */}
          <span className="hidden sm:flex lg:hidden xl:flex items-center gap-3">
            <span aria-hidden="true" className="h-10 w-px bg-white/20" />
            <span className="text-[13px] leading-[1.45] tracking-wide font-bold text-white font-space-grotesk">
              Enterprise AI
              <br />
              Learning Platform
            </span>
          </span>
        </Link>

        {/* Stretched to the full bar height (the row is a fixed h-20) so each menu's
                    highlight bar can sit on the navbar's bottom edge, right above the panel. */}
        <div className="hidden lg:flex self-stretch items-stretch gap-6 xl:gap-8">
          {VISIBLE_MENUS.map((menu) => {
            const isOpen = openMenu === menu.label;
            const active = isMenuActive(pathname, menu);
            return (
              <button
                key={menu.label}
                type="button"
                aria-expanded={isOpen}
                aria-controls="nav-mega-panel"
                onClick={() => setOpenMenu(isOpen ? null : menu.label)}
                // Always white on the dark bar; the open menu is marked by its blue
                // highlight bar below, not by a text colour.
                className="group/navitem relative flex items-center gap-1 font-bold font-space-grotesk text-lg whitespace-nowrap text-white"
              >
                {menu.label}
                <ChevronDown
                  aria-hidden="true"
                  className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
                {/* Highlight bar on the navbar's bottom edge: brand blue on hover and
                    while the menu is open (growing out from the centre on hover), a
                    faint white one for the menu whose page you are on. */}
                {active && !isOpen ? (
                  <span
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-[3px] bg-white/40"
                  />
                ) : null}
                <span
                  aria-hidden="true"
                  className={`absolute inset-x-0 bottom-0 h-[3px] bg-mkt-primary-container transition-transform duration-200 ${
                    isOpen
                      ? "scale-x-100"
                      : "scale-x-0 group-hover/navitem:scale-x-100"
                  }`}
                />
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden lg:flex items-center gap-4">
            {/* Signed out: the only action is Get started (no Log in). Signed in:
                            the visitor is already a customer, so their courses replace it. */}
            <SignedOut>
              <Link href={GET_STARTED_HREF} className={bookDemoClassName}>
                Get started
              </Link>
            </SignedOut>
            <SignedIn>
              {/* Without this the landing page is a one-way door: a signed-in
                                visitor can reach it but has nothing to click to get back. */}
              <Link
                href="/courses"
                className="text-white font-bold hover:text-mkt-primary transition-colors font-space-grotesk whitespace-nowrap"
              >
                My Courses
              </Link>
              {is_expert ? (
                <Link
                  href="/admin"
                  className="text-white font-bold hover:text-mkt-primary transition-colors font-space-grotesk"
                >
                  {user?.firstName || "Dashboard"}
                </Link>
              ) : null}
              <UserButton />
            </SignedIn>
          </div>

          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center text-white hover:text-mkt-primary transition-colors"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-menu"
            onClick={() =>
              isMobileMenuOpen ? closeMobileMenu() : setIsMobileMenuOpen(true)
            }
          >
            {/* SVG icons, not the Material Symbols font: until that font loads (or if
                its subset lacks a glyph) the ligature shows as the raw word "menu". */}
            {isMobileMenuOpen ? (
              <X aria-hidden="true" className="h-8 w-8" />
            ) : (
              <Menu aria-hidden="true" className="h-8 w-8" />
            )}
          </button>
        </div>
      </div>

      {/* Mega menu panel (desktop): a full-width white section under the bar, in place of
                a dropdown. The heading links to the menu's own page; the items fill columns,
                or, for a menu with sections, stack under their section's title. */}
      {openMenuData ? (
        <div
          id="nav-mega-panel"
          className="hidden lg:block absolute inset-x-0 top-full bg-white border-b border-mkt-outline-variant/40 shadow-[0_16px_32px_rgba(15,23,42,0.12)] px-margin-desktop"
        >
          <div className="max-w-[1440px] mx-auto py-size-lg">
            <Link
              href={openMenuData.href}
              onClick={closeMegaMenu}
              className="group inline-flex items-center gap-2 font-space-grotesk text-2xl font-bold text-mkt-inarax-black hover:text-mkt-primary-container transition-colors"
            >
              {openMenuData.heading ?? openMenuData.label}
              <ArrowRight
                aria-hidden="true"
                className="h-6 w-6 text-mkt-primary-container transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
            {"sections" in openMenuData ? (
              // One equal column per section.
              <div
                className="mt-size-md grid gap-x-size-lg"
                style={{ gridTemplateColumns: `repeat(${openMenuData.sections.length}, minmax(0, 1fr))` }}
              >
                {openMenuData.sections.map((section) => (
                  <div key={section.title}>
                    <Link
                      href={section.href}
                      onClick={closeMegaMenu}
                      // Same type as the other menus' items (MegaMenuLink).
                      className="font-space-grotesk font-semibold text-mkt-on-surface underline-offset-4 decoration-mkt-primary-container hover:text-mkt-primary-container hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mkt-primary-container rounded-lg"
                    >
                      {section.title}
                    </Link>
                    {section.items.length > 0 ? (
                      <ul className="list-none pl-0 ml-0 mt-size-sm flex flex-col gap-size-md">
                        {section.items.map((item) => (
                          <li key={item.label}>
                            <MegaMenuLink item={item} onNavigate={closeMegaMenu} />
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <ul className="list-none pl-0 ml-0 mt-size-md grid grid-cols-3 xl:grid-cols-4 gap-x-size-lg gap-y-size-md">
                {openMenuData.items.map((item) => (
                  <li key={item.label}>
                    <MegaMenuLink item={item} onNavigate={closeMegaMenu} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      {isMobileMenuOpen ? (
        <div
          id="mobile-nav-menu"
          className="lg:hidden max-h-[calc(100svh_-_5rem)] overflow-y-auto border-t border-mkt-on-surface/5 bg-mkt-surface px-margin-mobile py-4 flex flex-col gap-1"
        >
          {VISIBLE_MENUS.map((menu) => {
            const isOpen = openMobileMenu === menu.label;
            const panelId = `mobile-nav-${menu.label.toLowerCase().replace(/\s+/g, "-")}`;
            return (
              <div key={menu.label}>
                <div className="flex items-center justify-between">
                  <Link
                    href={menu.href}
                    onClick={closeMobileMenu}
                    className="flex-1 py-2 font-space-grotesk font-bold text-mkt-on-surface hover:text-mkt-primary transition-colors"
                  >
                    {menu.label}
                  </Link>
                  <button
                    type="button"
                    aria-label={`${isOpen ? "Hide" : "Show"} ${menu.label} menu`}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() =>
                      setOpenMobileMenu(isOpen ? null : menu.label)
                    }
                    className="p-2 -mr-2 text-mkt-on-surface hover:text-mkt-primary transition-colors"
                  >
                    <ChevronDown
                      aria-hidden="true"
                      className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>
                {isOpen ? (
                  <ul
                    id={panelId}
                    className="list-none pl-size-sm ml-0 pb-2 flex flex-col"
                  >
                    {"sections" in menu
                      ? menu.sections.map((section) => (
                          <li key={section.title} className="pt-2">
                            <Link
                              href={section.href}
                              onClick={closeMobileMenu}
                              className="block py-1 text-xs font-bold uppercase tracking-widest text-mkt-primary font-space-grotesk"
                            >
                              {section.title}
                            </Link>
                            {section.items.length > 0 ? (
                              <ul className="list-none pl-0 ml-0 flex flex-col">
                                {section.items.map((item) => (
                                  <li key={item.label}>
                                    <MobileMenuLink item={item} onNavigate={closeMobileMenu} />
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </li>
                        ))
                      : menu.items.map((item) => (
                          <li key={item.label}>
                            <MobileMenuLink item={item} onNavigate={closeMobileMenu} />
                          </li>
                        ))}
                  </ul>
                ) : null}
              </div>
            );
          })}

          <div className="mt-2 pt-2 border-t border-mkt-on-surface/5 flex flex-col gap-1">
            <SignedOut>
              <Link
                href={GET_STARTED_HREF}
                onClick={closeMobileMenu}
                className={`${bookDemoClassName} mt-2 text-center`}
              >
                Get started
              </Link>
            </SignedOut>
            <SignedIn>
              <Link
                href="/courses"
                onClick={closeMobileMenu}
                className="text-mkt-on-surface-variant hover:text-mkt-primary transition-colors py-2 font-space-grotesk"
              >
                My Courses
              </Link>
              <div className="py-2 flex items-center justify-between">
                {is_expert ? (
                  <Link
                    href="/admin"
                    onClick={closeMobileMenu}
                    className="text-mkt-on-surface-variant hover:text-mkt-primary transition-colors font-space-grotesk"
                  >
                    {user?.firstName || "Dashboard"}
                  </Link>
                ) : (
                  <span className="text-mkt-on-surface-variant font-space-grotesk">
                    {user?.firstName || "Account"}
                  </span>
                )}
                <UserButton />
              </div>
            </SignedIn>
          </div>
        </div>
      ) : null}
    </nav>
  );
}
